import type { DeliveryTrackerStatus } from '@kerberos-compliance/compliance-shared-ts/features/delivery/enums/delivery-tracker-status.enum';
import { DeliveryTrackerStatusMap } from '@kerberos-compliance/compliance-shared-ts/features/delivery/enums/delivery-tracker-status.enum';
import type { ReminderTrackerStatus } from '@kerberos-compliance/compliance-shared-ts/features/reminder-tracker/enums/reminder-tracker.enums';
import { ReminderTrackerStatusMap } from '@kerberos-compliance/compliance-shared-ts/features/reminder-tracker/enums/reminder-tracker.enums';

export const ReminderTrackerBlockReasonMap = {
  None: 'none',
  InactiveContract: 'inactiveContract',
  NoActiveUser: 'noActiveUser',
} as const;

export type ReminderTrackerBlockReason =
  (typeof ReminderTrackerBlockReasonMap)[keyof typeof ReminderTrackerBlockReasonMap];

export type ResolveReminderTrackerBlockReasonParameters = {
  reminderTrackerStatus?: ReminderTrackerStatus;
  deliveryTrackerStatus?: DeliveryTrackerStatus;
  isContractInactive?: boolean;
  hasActiveNotifiableUser: boolean;
};

/**
 * Resolves why a reminder is stopped from the data already loaded for the delivery tracker.
 * Reasons are evaluated in priority order; a completed reminder is never considered stopped.
 *
 * @param {ResolveReminderTrackerBlockReasonParameters} params - The reminder/delivery tracker context.
 * @param {ReminderTrackerStatus} [params.reminderTrackerStatus] - The stored reminder tracker status, if a row exists.
 * @param {DeliveryTrackerStatus} [params.deliveryTrackerStatus] - The owning delivery tracker status.
 * @param {boolean} [params.isContractInactive] - Whether the organization's contract is inactive (state not Active).
 * @param {boolean} params.hasActiveNotifiableUser - Whether the organization has an active RiskAnalysisCustomer user.
 * @returns {ReminderTrackerBlockReason} The active block reason, or `None` when the reminder is not blocked.
 */
export function resolveReminderTrackerBlockReason({
  reminderTrackerStatus,
  deliveryTrackerStatus,
  isContractInactive,
  hasActiveNotifiableUser,
}: ResolveReminderTrackerBlockReasonParameters): ReminderTrackerBlockReason {
  if (reminderTrackerStatus === ReminderTrackerStatusMap.Done) {
    return ReminderTrackerBlockReasonMap.None;
  }
  if (isContractInactive || deliveryTrackerStatus === DeliveryTrackerStatusMap.InactiveContract) {
    return ReminderTrackerBlockReasonMap.InactiveContract;
  }
  if (!hasActiveNotifiableUser) {
    return ReminderTrackerBlockReasonMap.NoActiveUser;
  }
  return ReminderTrackerBlockReasonMap.None;
}
